import * as Sentry from '@sentry/nextjs'
import { IconCopy } from '@tabler/icons-react'
import { message } from 'antd'
import CopyToClipboard from 'react-copy-to-clipboard'

import { Button } from '../shared-components/BUI/Button'
import { Tag } from '../shared-components/BUI/Tag'
import { Text } from '../shared-components/BUI/Text'
import { ErrorIcon } from '../shared-components/ErrorIcon'
import { Flexbox } from '../shared-components/Flexbox'

import { openLiveChat } from './live-chat/LiveChat'

import config from '@/config/index'

import { buildPassTheme } from 'src/styles/theme'

type Props = {
  eventId: string
}

const Error: React.FC<Props> = ({ eventId }) => {
  return (
    <Flexbox
      flexDirection="column"
      gap="1rem"
      justifyContent="center"
      alignItems="center"
      mt="4rem"
    >
      <ErrorIcon />

      <Text
        variant="heading03"
        align="center"
        style={{ fontWeight: 400 }}
        mb="1rem"
      >
        Oh no!{' '}
        <Text noVariant style={{ whiteSpace: 'nowrap' }}>
          We&apos;ve hit an unexpected error
        </Text>
      </Text>

      <Text variant="label02" secondary="_500" align="center">
        The BuildPass team has been notified.{' '}
        <Text noVariant style={{ whiteSpace: 'nowrap' }}>
          Please try again shortly.
        </Text>
      </Text>

      <Text variant="label02" secondary="_500" align="center">
        If you require further assistance use our live chat feature or contact
        us at{' '}
        <a href={`mailto:${config.supportEmail}?subject=Error: ${eventId}`}>
          {config.supportEmail}
        </a>
      </Text>

      {eventId && (
        <CopyToClipboard
          text={eventId}
          onCopy={() => message.success('Error code copied!')}
        >
          <Flexbox
            flexDirection="column"
            alignItems="center"
            gap="0.5rem"
            mt="1rem"
          >
            <Text variant="caption02" secondary="_500">
              Error ID
            </Text>
            <Tag
              label={
                <>
                  {eventId}{' '}
                  <Button
                    variant="secondary-text"
                    shape="circle"
                    size="small"
                    icon={
                      <IconCopy
                        size={16}
                        color={buildPassTheme.colour.secondary._500}
                      />
                    }
                  />
                </>
              }
              capitalise={false}
              strong={false}
            />
          </Flexbox>
        </CopyToClipboard>
      )}
    </Flexbox>
  )
}

type BoundaryProps = {
  children?: React.ReactNode
}

const ErrorBoundary: React.FC<BoundaryProps> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      onError={openLiveChat}
      fallback={(options) => <Error eventId={options.eventId} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

export default ErrorBoundary
